import { EscalationListSummaryConstants } from "../config/escalationConfig";
import { ESCALATION_FIELD_NAME_MAP } from "./escalationFields";


function buildEscalationSummaryForRule(rule = {}, opts = {}) {
  const OP_MAP = {
    equals: "=",
    not_equals: "!=",
    in: "IN",
    not_in: "NOT IN",
    contains: "contains",
    not_contains: "does not contain",
    greater_than: ">",
    greater_than_or_equal: ">=",
    less_than: "<",
    less_than_or_equal: "<=",
    starts_with: "starts with",
    ends_with: "ends with",
    is_null: "is null",
    is_not_null: "is not null",
  };

  function friendlyFieldLabel(fieldKey) {
    if (!fieldKey) return "";
    return (
      ESCALATION_FIELD_NAME_MAP[fieldKey] ||
      String(fieldKey)
        .replace(/([A-Z])/g, " $1")
        .replace(/[_\-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(
          /\w\S*/g,
          (s) => s.charAt(0).toUpperCase() + s.substring(1).toLowerCase()
        )
    );
  }

  function formatValue(val, dataType) {
    if (val === null || val === undefined) return String(val);
    if (Array.isArray(val))
      return val.map((v) => formatValue(v, dataType)).join(", ");
    if (typeof val === "boolean") return val ? "Yes" : "No";
    if (
      dataType === "DATE" ||
      dataType === "DATETIME" ||
      (typeof val === "string" && /\d{4}-\d{2}-\d{2}T/.test(val))
    ) {
      const d = new Date(val);
      if (!isNaN(d)) return d.toLocaleString();
    }
    return String(val);
  }

  // ---------- CONDITION SUMMARY ----------
  function renderCondition(cond = {}) {
    // const fieldKey = cond.field_code || cond.field_name;
    // const label = friendlyFieldLabel(fieldKey);
    const label = cond.field_name;
    const opKey = cond.operator || cond.op;
    const operator = OP_MAP[opKey] || opKey;
    if (operator === "is null" || operator === "is not null") {
      return `${label} ${operator}`;
    }
    const value = formatValue(cond.value, cond.dataType || cond.data_type);
    return cond.dataType === "DATE" || cond.dataType === "DATETIME" || cond.dataType === "BOOLEAN" ? `${label} ${operator} "${value}"` : `${label} ${operator} "${cond.valueLabel}"`;
  }
  

  function renderGroup(group = {}) {
    const conds = (group.conditions || []).map(renderCondition).filter(Boolean);
    if (conds.length === 0) return "";
    const internal = (
      group.internalOperator || group.internal_operator
    ).toUpperCase();
    const joiner = internal === "OR" ? " Or " : " And ";
    // if (conds.length === 1) return conds[0];
    return `(${conds.join(joiner)})`;
  }

  function humanizeConditions(additionalConditions = {}) {
    if (
      !additionalConditions ||
      !Array.isArray(additionalConditions.groups) ||
      additionalConditions.groups.length === 0
    )
      return "";
    const parts = [];
    const groups = additionalConditions.groups;
    for (let i = 0; i < groups.length; i++) {
      const g = groups[i];
      const txt = renderGroup(g);
      if (!txt) continue;
      parts.push(txt);
      if (g.nextOperator && i < groups.length - 1) {
        parts.push(g.nextOperator.toUpperCase() === "OR" ? "Or" : "And");
      }
    }
    return parts.join(" ");
  }

  // ---------- METADATA LOOKUPS ----------
  const { shortMax = 120 } = opts || {};
  const catName =
    rule.category_config_id != null ? `${rule.category_name}` : null;
  const statusName = rule.status != null ? `${rule.status_name}` : null;
  const ticketTypeName = rule.ticket_type || null;

  const condObj = rule.additional_conditions || rule.additionalConditions || {};
  const condSentence = humanizeConditions(condObj);

  const prefixParts = [];
  if (ticketTypeName) prefixParts.push(` type is "${ticketTypeName}"`);
  if (catName) prefixParts.push(` category as "${catName}"`);
  if (statusName) prefixParts.push(` status as "${statusName}"`);

  let full = EscalationListSummaryConstants.BaseSummary;
  if (prefixParts.length > 0) {
    full += `${prefixParts.join("; ")}`;
  }
  if (condSentence) {
    full += `; ${condSentence}`;
  } else {
    full += `; ${EscalationListSummaryConstants.NoConditions}`;
  }

  // ---------- ACTION SUMMARY ----------
  let actionSummary = "";
  const { execution_mode } = rule || {};
  if (execution_mode === "scheduled" && rule.time_trigger_enabled) {
    const hours = rule.time_trigger_hours;
    const minutes = rule.time_trigger_minutes;
    const reference = rule.time_trigger_reference;

    const parts = [];
    if (hours > 0) parts.push(`${hours} hour${hours > 1 ? "s" : ""}`);
    if (minutes > 0) parts.push(`${minutes} minute${minutes > 1 ? "s" : ""}`);

    // const timePhrase =
    //   parts.length > 0 ? `after ${parts.join(" ")}` : "immediately (0 minutes)";
    actionSummary = `${EscalationListSummaryConstants.Scheduled} ${parts.join(" ")} ${
      reference === "due_time" ? "before" : "after"
    } ${
      reference === "due_time"
        ? "its due time"
        : `${
            reference === "last_modified"
              ? "its last modified time"
              : "its creation time"
          }`
    }`;
  } else if (execution_mode === "ticket_creation") {
    actionSummary = `${EscalationListSummaryConstants.TicketCreation}`;
  } else if (execution_mode === "ticket_update") {
    actionSummary = `${EscalationListSummaryConstants.TicketUpdate}`;
  }

  // Append actions (if any)
  const actions = Array.isArray(rule.ticket_actions)
    ? rule.ticket_actions.map((a) => {
        // const label = friendlyFieldLabel(a.field_code || a.field_name);
        const label = a.field_name;
        // const val = formatValue(a.value);
        const val = a.valueLabel || a.value ;
        return `${label} = "${val}"`;
      })
    : [];
  if (actions.length > 0) {
    actionSummary += `; ${EscalationListSummaryConstants.SetTheFollowingFields}: ${actions.join(", ")}`;
  }

  const notifications = rule.notification_method
    ? `; ${EscalationListSummaryConstants.Notifies}: ${EscalationListSummaryConstants.EndUserNotificationSentTo} "${rule.notification_method === "email" ? "Email" : `${rule.notification_method === "push" ? "Push" : "Email & Push"}`}"`
    : "";

  actionSummary += notifications;

  let short = full;
  if (short.length > shortMax) {
    short = short.slice(0, shortMax - 3).replace(/\s+\S*$/, "") + "...";
  }

  return {
    additional_conditions_summary: short,
    additional_conditions_full_summary: full,
    actions_summary: actionSummary,
  };
}

export default buildEscalationSummaryForRule;
